/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { EInvoiceAddressOptionsResponse } from '../models/e-invoice-address-options-response';


/**
 * Resolve a recipient's Apix eInvoice routing candidates by business id
 * and (given a stored routing) report whether it is still registered.
 */
@Injectable({
  providedIn: 'root',
})
export class EinvoiceAddressLookupService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation getEinvoiceAddressOptions
   */
  static readonly GetEinvoiceAddressOptionsPath = '/einvoice-address-options';

  /**
   * Resolve eInvoice address routing candidates.
   *
   * Resolve a recipient's Apix eInvoice routing candidates by business id
   * via the Apix `addressquery` transport. When the optional stored
   * `address` and `operator` query parameters are supplied, each candidate
   * is annotated with `matches_stored` and the response reports
   * `stored_routing_present`, both computed by the single canonical
   * server-side match rule. The match keys on the välittäjätunnus
   * (`operator`), never on the display-only Apix operator code
   * (`operator_id`). Zero candidates
   * for a supplied stored routing means `stored_routing_present` is false
   * (the recipient has dropped off the network-invoice grid).
   *
   * Requires authentication only; no additional permission. An Apix
   * failure is non-fatal for the caller — it surfaces a distinct
   * `502`/`apix_unavailable` error so the Console can degrade to manual
   * routing entry rather than masquerading as an empty result.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getEinvoiceAddressOptions()` instead.
   *
   * This method doesn't expect any request body.
   */
  getEinvoiceAddressOptions$Response(params: {

    /**
     * Recipient business id (y-tunnus) to resolve routing for.
     */
    business_id: string;

    /**
     * Stored eInvoice address to check against the live candidates. When
     * supplied together with &#x60;operator&#x60;, drives &#x60;matches_stored&#x60; /
     * &#x60;stored_routing_present&#x60;.
     */
    address?: string;

    /**
     * Stored eInvoice operator (välittäjätunnus / intermediator id) to
     * check against the live candidates. Matched against each candidate&#x27;s
     * &#x60;operator&#x60;, not the display-only Apix operator code &#x60;operator_id&#x60;.
     */
    operator?: string;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<EInvoiceAddressOptionsResponse>> {

    const rb = new RequestBuilder(this.rootUrl, EinvoiceAddressLookupService.GetEinvoiceAddressOptionsPath, 'get');
    if (params) {
      rb.query('business_id', params.business_id, {});
      rb.query('address', params.address, {});
      rb.query('operator', params.operator, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EInvoiceAddressOptionsResponse>;
      })
    );
  }

  /**
   * Resolve eInvoice address routing candidates.
   *
   * Resolve a recipient's Apix eInvoice routing candidates by business id
   * via the Apix `addressquery` transport. When the optional stored
   * `address` and `operator` query parameters are supplied, each candidate
   * is annotated with `matches_stored` and the response reports
   * `stored_routing_present`, both computed by the single canonical
   * server-side match rule. The match keys on the välittäjätunnus
   * (`operator`), never on the display-only Apix operator code
   * (`operator_id`). Zero candidates
   * for a supplied stored routing means `stored_routing_present` is false
   * (the recipient has dropped off the network-invoice grid).
   *
   * Requires authentication only; no additional permission. An Apix
   * failure is non-fatal for the caller — it surfaces a distinct
   * `502`/`apix_unavailable` error so the Console can degrade to manual
   * routing entry rather than masquerading as an empty result.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getEinvoiceAddressOptions$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getEinvoiceAddressOptions(params: {

    /**
     * Recipient business id (y-tunnus) to resolve routing for.
     */
    business_id: string;

    /**
     * Stored eInvoice address to check against the live candidates. When
     * supplied together with &#x60;operator&#x60;, drives &#x60;matches_stored&#x60; /
     * &#x60;stored_routing_present&#x60;.
     */
    address?: string;

    /**
     * Stored eInvoice operator (välittäjätunnus / intermediator id) to
     * check against the live candidates. Matched against each candidate&#x27;s
     * &#x60;operator&#x60;, not the display-only Apix operator code &#x60;operator_id&#x60;.
     */
    operator?: string;
    context?: HttpContext
  }
): Observable<EInvoiceAddressOptionsResponse> {

    return this.getEinvoiceAddressOptions$Response(params).pipe(
      map((r: StrictHttpResponse<EInvoiceAddressOptionsResponse>) => r.body as EInvoiceAddressOptionsResponse)
    );
  }

}
